import type { CSSProperties } from "react";
import { minutesIntoDay } from "../../lib/date.js";
import type { CalendarEvent } from "./calendar.types.js";

/**
 * Pure layout math for the Day / Week time grid — Plan 0002 §7.
 *
 * Kept separate from the React component so the bug-prone parts (the
 * overlap column-packer especially) are unit-testable with no DOM.
 */

// The visible window covers a generous dealership day; events outside
// it clamp to the edges. Fixed constants, not props — a different
// window is a real prop the day a consumer needs one, not before.
export const DAY_START_HOUR = 7;
const DAY_END_HOUR = 21;
// Each hour is a tall band (112px) so the day grid is easy to read
// and tap; the time-grid viewport is sized to ~6 of these and scrolls
// for the rest of the day.
export const HOUR_HEIGHT = 112; // px
/** Hours the time-grid viewport shows before scrolling. */
export const VISIBLE_HOURS = 6;
export const MIN_BLOCK_HEIGHT = 22; // px — keeps tiny events tappable
const TASK_SPAN_MIN = 30; // layout span for point-in-time tasks
export const WINDOW_START_MIN = DAY_START_HOUR * 60;
export const WINDOW_END_MIN = DAY_END_HOUR * 60;
export const GRID_HEIGHT = (DAY_END_HOUR - DAY_START_HOUR) * HOUR_HEIGHT;

/** Hour numbers spanning the visible window — the gutter + gridlines. */
export const HOURS: number[] = Array.from(
  { length: DAY_END_HOUR - DAY_START_HOUR },
  (_, i) => DAY_START_HOUR + i,
);

/**
 * Default dealership business hours PER WEEKDAY, shaded as a band on
 * the time grid so operators see at a glance when sales / service are
 * open. Each array is indexed by `Date.getDay()` (0 = Sunday …
 * 6 = Saturday); a `null` entry means the department is closed that
 * day, so no band is drawn. Sensible defaults — a per-rooftop override
 * is a real prop the day a consumer needs one, not before.
 */
// Band edges are minutes from midnight (15-minute granularity), so a
// rooftop that opens at e.g. 8:30 shades correctly.
export interface HourBand {
  startMinute: number;
  endMinute: number;
}

export const SALES_HOURS_BY_DAY: ReadonlyArray<HourBand | null> = [
  null, // Sun — closed
  { startMinute: 480, endMinute: 1140 }, // Mon 8:00 AM – 7:00 PM
  { startMinute: 480, endMinute: 1140 }, // Tue
  { startMinute: 480, endMinute: 1140 }, // Wed
  { startMinute: 480, endMinute: 1140 }, // Thu
  { startMinute: 480, endMinute: 1140 }, // Fri
  { startMinute: 540, endMinute: 1080 }, // Sat 9:00 AM – 6:00 PM
];

export const SERVICE_HOURS_BY_DAY: ReadonlyArray<HourBand | null> = [
  null, // Sun — closed
  { startMinute: 420, endMinute: 1080 }, // Mon 7:00 AM – 6:00 PM
  { startMinute: 420, endMinute: 1080 }, // Tue
  { startMinute: 420, endMinute: 1080 }, // Wed
  { startMinute: 420, endMinute: 1080 }, // Thu
  { startMinute: 420, endMinute: 1080 }, // Fri
  { startMinute: 480, endMinute: 840 }, // Sat 8:00 AM – 2:00 PM
];

/** Absolute top + height (px) for a minutes-of-day range in the grid. */
export function minuteBandStyle(
  startMinute: number,
  endMinute: number,
): CSSProperties {
  const topMin = Math.max(startMinute, WINDOW_START_MIN);
  const botMin = Math.min(endMinute, WINDOW_END_MIN);
  return {
    top: ((topMin - WINDOW_START_MIN) / 60) * HOUR_HEIGHT,
    height: (Math.max(botMin - topMin, 0) / 60) * HOUR_HEIGHT,
  };
}

export interface PlacedEvent {
  event: CalendarEvent;
  startMin: number;
  endMin: number;
  /** Column index within the overlap cluster. */
  lane: number;
  /** Total columns the cluster spans — every member shares this. */
  lanes: number;
}

/** Actual [start, end] minutes for overlap math; tasks get a nominal span. */
export function eventBounds(event: CalendarEvent): [number, number] {
  const start = minutesIntoDay(event.start);
  const rawEnd = event.end ? minutesIntoDay(event.end) : start + TASK_SPAN_MIN;
  return [start, Math.min(Math.max(rawEnd, start + 15), 24 * 60)];
}

/**
 * Column-packing: events that overlap in time are split into
 * side-by-side lanes. Events are grouped into clusters (maximal
 * transitively-overlapping sets); within a cluster every event takes
 * the first lane free at its start time, and all members share the
 * cluster's lane count so widths line up.
 */
export function layoutColumn(events: CalendarEvent[]): PlacedEvent[] {
  const items = events
    .map((event) => {
      const [startMin, endMin] = eventBounds(event);
      return { event, startMin, endMin };
    })
    .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

  const placed: PlacedEvent[] = [];
  let cluster: typeof items = [];
  let clusterEnd = -1;

  const flush = (): void => {
    if (cluster.length === 0) return;
    const laneEnds: number[] = [];
    const laneByItem = new Map<(typeof cluster)[number], number>();
    for (const item of cluster) {
      let lane = laneEnds.findIndex((end) => end <= item.startMin);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(item.endMin);
      } else {
        laneEnds[lane] = item.endMin;
      }
      laneByItem.set(item, lane);
    }
    for (const item of cluster) {
      placed.push({
        event: item.event,
        startMin: item.startMin,
        endMin: item.endMin,
        lane: laneByItem.get(item) ?? 0,
        lanes: laneEnds.length,
      });
    }
    cluster = [];
    clusterEnd = -1;
  };

  for (const item of items) {
    if (cluster.length > 0 && item.startMin >= clusterEnd) flush();
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.endMin);
  }
  flush();
  return placed;
}

/** Absolute top/height/left/width for a placed event, clamped to the window. */
export function blockStyle(placed: PlacedEvent): CSSProperties {
  const topMin = Math.min(
    Math.max(placed.startMin, WINDOW_START_MIN),
    WINDOW_END_MIN,
  );
  const botMin = Math.min(Math.max(placed.endMin, topMin), WINDOW_END_MIN);
  const height = Math.max(
    ((botMin - topMin) / 60) * HOUR_HEIGHT,
    MIN_BLOCK_HEIGHT,
  );
  // Cap top after height is known so the min-height sliver of an
  // after-window event pins to the grid's bottom edge instead of
  // painting below the last gridline.
  const top = Math.min(
    ((topMin - WINDOW_START_MIN) / 60) * HOUR_HEIGHT,
    GRID_HEIGHT - height,
  );
  const widthPct = 100 / placed.lanes;
  return {
    top,
    height,
    left: `${placed.lane * widthPct}%`,
    width: `calc(${widthPct}% - 3px)`,
  };
}
